import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Ticket, Loader2, Calendar, MapPin, QrCode, AlertCircle, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import {
  appsBillingApiTicketsGetMyTickets,
  appsBillingApiTicketsGetTicketQrUrl,
} from '@/lib/api';
import type { TicketWithEventResponse } from '@/lib/api/generated/models';
import { isStripeEnabled } from '@/lib/features';

interface TicketsTabProps {
  isOwnProfile: boolean;
}

/**
 * TicketsTab displays the user's purchased rally tickets on their profile.
 * Only visible on the user's own profile.
 */
const TicketsTab = ({ isOwnProfile }: TicketsTabProps) => {
  const { toast } = useToast();
  const [tickets, setTickets] = useState<TicketWithEventResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // QR Modal state
  const [selectedTicket, setSelectedTicket] = useState<TicketWithEventResponse | null>(null);
  const [qrUrl, setQrUrl] = useState<string | null>(null);
  const [qrLoading, setQrLoading] = useState(false);

  const stripeEnabled = isStripeEnabled();

  // Fetch tickets on mount (only when viewing own profile and Stripe is enabled)
  useEffect(() => {
    if (!isOwnProfile || !stripeEnabled) {
      setLoading(false);
      return;
    }

    const fetchTickets = async () => {
      try {
        const response = await appsBillingApiTicketsGetMyTickets();
        setTickets(response.data.tickets);
      } catch (err: any) {
        console.error('Failed to fetch tickets:', err);
        setError(err.message || 'Failed to load tickets');
      } finally {
        setLoading(false);
      }
    };

    fetchTickets();
  }, [isOwnProfile, stripeEnabled]);

  // Only show on own profile when Stripe is enabled - must be after all hooks
  if (!isOwnProfile || !stripeEnabled) {
    return null;
  }

  const handleViewQR = async (ticket: TicketWithEventResponse) => {
    if (!ticket.has_qr_code) {
      toast({
        title: 'QR Code Not Available',
        description: 'This ticket does not have a QR code yet.',
        variant: 'destructive',
      });
      return;
    }

    setSelectedTicket(ticket);
    setQrUrl(null);
    setQrLoading(true);

    try {
      const response = await appsBillingApiTicketsGetTicketQrUrl(ticket.id);
      setQrUrl(response.data.qr_url);
    } catch (err: any) {
      console.error('Failed to fetch QR code:', err);
      toast({
        title: 'Error',
        description: 'Failed to load QR code. Please try again.',
        variant: 'destructive',
      });
      setSelectedTicket(null);
    } finally {
      setQrLoading(false);
    }
  };

  const closeQRModal = () => {
    setSelectedTicket(null);
    setQrUrl(null);
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'Date TBD';
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid':
        return 'text-green-400 bg-green-400/10';
      case 'pending':
        return 'text-yellow-400 bg-yellow-400/10';
      case 'refunded':
        return 'text-red-400 bg-red-400/10';
      case 'canceled':
        return 'text-gray-400 bg-gray-400/10';
      default:
        return 'text-adv-lightGray bg-white/10';
    }
  };

  if (loading) {
    return (
      <div className="glass-card rounded-lg p-6">
        <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
          <Ticket className="h-5 w-5 text-adv-orange" />
          My Tickets
        </h2>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-adv-orange" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="glass-card rounded-lg p-6">
        <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
          <Ticket className="h-5 w-5 text-adv-orange" />
          My Tickets
        </h2>
        <div className="flex items-center gap-2 text-red-400 py-4">
          <AlertCircle className="h-5 w-5" />
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="glass-card rounded-lg p-6">
        <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
          <Ticket className="h-5 w-5 text-adv-orange" />
          My Tickets
        </h2>

        {tickets.length === 0 ? (
          <div className="text-center py-8">
            <Ticket className="h-12 w-12 text-adv-lightGray mx-auto mb-3 opacity-50" />
            <p className="text-adv-lightGray mb-4">You haven't purchased any rally tickets yet.</p>
            <Link to="/rallies">
              <Button variant="outline" className="text-sm px-3 py-1">
                Browse Rallies
              </Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {tickets.map((ticket) => (
              <div
                key={ticket.id}
                className="border border-white/10 rounded-lg p-4 hover:border-white/20 transition-colors"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <Link
                      to={`/rallies/${ticket.event_id}`}
                      className="font-semibold text-white hover:text-adv-orange transition-colors block truncate"
                    >
                      {ticket.event_title}
                    </Link>
                    <p className="text-sm text-adv-lightGray mt-1">{ticket.tier_name}</p>

                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-sm text-adv-lightGray">
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3.5 w-3.5" />
                        {formatDate(ticket.event_meeting_datetime)}
                      </span>
                      {ticket.event_meeting_location && (
                        <span className="flex items-center gap-1 truncate">
                          <MapPin className="h-3.5 w-3.5" />
                          {ticket.event_meeting_location}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col items-end gap-2">
                    <span
                      className={`text-xs font-medium px-2 py-1 rounded-full capitalize ${getStatusColor(
                        ticket.status
                      )}`}
                    >
                      {ticket.status}
                    </span>
                    <span className="text-white font-medium">
                      ${ticket.amount_paid.toFixed(2)}
                    </span>
                  </div>
                </div>

                {ticket.status === 'paid' && ticket.has_qr_code && (
                  <div className="mt-3 pt-3 border-t border-white/10">
                    <Button
                      onClick={() => handleViewQR(ticket)}
                      variant="outline"
                      className="w-full sm:w-auto text-sm px-3 py-1"
                    >
                      <QrCode className="h-4 w-4 mr-2" />
                      View QR Code
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* QR Code Modal */}
      {selectedTicket && (
        <div
          className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
          onClick={closeQRModal}
        >
          <div
            className="glass-card rounded-lg p-6 max-w-sm w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-white">Ticket QR Code</h3>
              <button
                onClick={closeQRModal}
                className="text-adv-lightGray hover:text-white transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <p className="text-sm text-adv-lightGray mb-4 text-center">
              {selectedTicket.event_title} - {selectedTicket.tier_name}
            </p>

            <div className="flex justify-center mb-4">
              {qrLoading ? (
                <div className="w-48 h-48 bg-white/5 rounded-lg flex items-center justify-center">
                  <Loader2 className="h-8 w-8 animate-spin text-adv-orange" />
                </div>
              ) : qrUrl ? (
                <img
                  src={qrUrl}
                  alt="Ticket QR Code"
                  className="w-48 h-48 rounded-lg bg-white p-2"
                />
              ) : (
                <div className="w-48 h-48 bg-white/5 rounded-lg flex items-center justify-center">
                  <p className="text-adv-lightGray text-sm text-center px-4">
                    Failed to load QR code
                  </p>
                </div>
              )}
            </div>

            <p className="text-xs text-adv-lightGray text-center">
              Show this QR code at check-in
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default TicketsTab;
